import { useLayoutEffect, useRef, useState } from "react";
import {
  ChoiceFan,
  stackFromWidth,
} from "@bindery/ui/components/reader/choice-fan";
import { useElementWidth } from "@bindery/ui/hooks/use-element-width";
import { cn } from "@bindery/ui/lib/utils";
import type { ReferenceGate } from "./reference-lookup";

type MatchingGate = Extract<ReferenceGate, { status: "matching" }>;

const HARD_MISS = 2;
const STAGGER_STEP_MS = 150;
const CARD_CLASS =
  "min-h-14 rounded-2xl border-[3px] px-2 py-3 font-serif text-lg font-semibold tracking-[0.06em]";

export function ReferenceChoiceCards({
  gate,
  word,
  lang,
  onSelect,
}: {
  gate: MatchingGate;
  word: string;
  lang: string;
  onSelect: (id: number) => void;
}) {
  const locked = gate.phase !== "idle";
  const hard = gate.wrongCount >= HARD_MISS;
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(containerEl);
  const [naturalWidth, setNaturalWidth] = useState(0);
  useLayoutEffect(() => {
    if (measureRef.current) setNaturalWidth(measureRef.current.scrollWidth);
  }, [gate.choices, availableWidth]);
  const stacked = stackFromWidth(naturalWidth, availableWidth);
  return (
    <div className="relative">
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute top-0 left-0 inline-flex gap-3 whitespace-nowrap"
      >
        {gate.choices.map((chip) => (
          <span key={chip.id} className={CARD_CLASS}>
            {chip.form}
          </span>
        ))}
      </div>
      <ChoiceFan
        choices={gate.choices}
        getKey={(chip) => chip.id}
        isSelected={(chip) => gate.selectedId === chip.id}
        locked={locked}
        stacked={stacked}
        stagger={{
          active: gate.phase === "staggerReturn" && hard,
          stepMs: STAGGER_STEP_MS,
        }}
        containerRef={setContainerEl}
        containerProps={{
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a row of buttons laid out as a plain flex box, not a form control group
          role: "group",
          "aria-label": `Which one matches ${word}?`,
          className: cn(
            "mx-auto flex w-full justify-center gap-3",
            stacked ? "max-w-xs flex-col items-stretch" : "max-w-sm items-end",
          ),
        }}
        cardClassName={({ stacked: cardStacked }) =>
          cn(
            "transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none",
            cardStacked ? "flex-none" : "min-w-0 flex-1",
            gate.phase === "faded" &&
              (hard ? "opacity-0" : "translate-y-2 opacity-30"),
          )
        }
        cardStyle={({ staggerDelayMs }) => ({
          transitionDelay:
            staggerDelayMs === 0 ? undefined : `${String(staggerDelayMs)}ms`,
        })}
        renderCard={(chip) => {
          const selected = gate.selectedId === chip.id;
          return (
            <button
              type="button"
              lang={lang}
              aria-pressed={selected}
              // `aria-disabled`, never `disabled`: a disabled card drops the
              // reader's focus to the page on every miss.
              aria-disabled={locked}
              onClick={() => {
                if (!locked) onSelect(chip.id);
              }}
              className={cn(
                CARD_CLASS,
                "w-full transition-colors duration-150",
                "focus-visible:ring-ring/60 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                "aria-disabled:cursor-default",
                selected
                  ? locked
                    ? "border-destructive bg-destructive/8 text-foreground"
                    : "border-lookup bg-lookup/10 text-foreground"
                  : "border-rule bg-popover text-foreground hover:border-foreground/50",
                gate.phase === "shaking" &&
                  selected &&
                  (hard
                    ? "motion-safe:animate-gate-shake-hard"
                    : "motion-safe:animate-gate-shake-light"),
              )}
            >
              {chip.form}
            </button>
          );
        }}
      />
    </div>
  );
}
